import React, { useState, useEffect } from 'react'
import { startOfToday, format } from 'date-fns'

import '../lib/ui/Modal.css'

import './ProjectDetails.css'

import { DateInput, DateInputUpdate } from '../lib/ui/DateInput'
import { AVProject, AVProjectUnitType, AVProjectCutoffPoint, MagnoEntityType, ClientCodeType } from '../types/entities'
import { Route } from '../types/app'
import { ApproversSelector } from './ApproversSelector'
import { ProjectBinaryAttribute } from './ProjectBinaryAttribute'
import { loadProject, createProject, updateProject } from '../actions/asyncActions'
import { getSelectedValue, getSelectedValueOrNull } from '../helpers/SelectHelpers'
import { validateProjectInputs } from '../helpers/Validation'
import { routeToPath } from '../routing'
import { ProjectNullableNumberAttribute } from "./ProjectNullableNumberAttribute"

interface Props {
  route: Route
  navigate: Function
}

const defaultNewProject: AVProject = {
  ID: 0,
  ProjectCode: '',
  HoursCostCode: 0,
  ExpensesCostCode: 0,
  ProjectType: 'Purchase',
  StartDate: format(startOfToday(), 'yyyy-MM-dd'),
  EndDate: '',
  UnitType: null,
  ExpensesReimbursed: null,
  TravelReimbursed: null,
  ExpensesDeductible: null,
  TravelDeductible: null,
  TravelExpensesDeductible: null,
  PaidLeave: null,
  ApproverID: null,
  ApproverName: null,
  Approvers: [],
  MagnoEntity: 'MIR',
  CutoffPoint: null,
  Supplier: null,
  MaxDeductibleTravel: null,
  ClientCode: null,
  ExactSupplierCode: null,
  MIPProjectCode: null
}

const changeProjectType = (state: AVProject, e: any): AVProject => {
  const el = e.target as any
  const val = el.options[el.selectedIndex].value

  const resetState: AVProject = {
    ...state,
    UnitType: null,
    ExpensesReimbursed: null,
    TravelReimbursed: null,
    ExpensesDeductible: null,
    TravelDeductible: null,
    TravelExpensesDeductible: null,
    PaidLeave: null,
    CutoffPoint: null,
    ApproverID: null,
    ProjectType: val,
    MagnoEntity: 'MIR',
    Supplier: null,
    MaxDeductibleTravel: null,
    ClientCode: null,
    ExactSupplierCode: null
  }
  return resetState
}

const changeSupplier = (state: AVProject, e: any): AVProject => {
  const el = e.target as any
  const val = el.options[el.selectedIndex].value

  const resetState: AVProject = {
    ...state,
    ExpensesReimbursed: false,
    TravelReimbursed: false,
    ExpensesDeductible: false,
    TravelDeductible: false,
    TravelExpensesDeductible: false,
    PaidLeave: false,
    Supplier: val,
    MaxDeductibleTravel: null,
    ExactSupplierCode: null
  }
  return resetState
}

const loadAvProject = async (id: string, setProject: Function) => {
  const project = await loadProject(id)
  const editProject: AVProject = {
    ...project,
    StartDate: project.StartDate.substr(0, 10),
    EndDate: project.EndDate ? project.EndDate.substr(0, 10) : ''
  }
  setProject(editProject)
}

export const ProjectDetails = ({ route }: Props) => {
  const [state, setState] = useState({} as AVProject)
  const [error, setError] = useState('')
  const [approvers, setApprovers] = useState([] as string[])

  const professionalId = route.params.professional_id || state.AVProfessionalID

  useEffect(() => {
    if (route.params.id) {
      loadAvProject(route.params.id, (project: AVProject) => {
        setState(project)
        const approversIds: string[] = project.Approvers.map(approver => approver.ID!)
        setApprovers(approversIds)
      })
    } else {
      setState({
        ...defaultNewProject,
        AVProfessionalID: Number(professionalId)
      })
    }
  }, [route.params.id])

  const saveHandler = async () => {
    const maybeErr = validateProjectInputs(state)
    
    if (maybeErr) {
      return setError(maybeErr)
    }

    const project = {
      ...state,
      NewApproversList: approvers
    }
    if (state.ID) {
      updateProject(project)
    } else {
      createProject(project)
    }
  }

  const showExpensesControls = state.ProjectType === 'Sales'
        && (state.Supplier === 'ComPas' || state.Supplier === 'MIR')

  return <div>
    <div className='top-nav'>
      <button
        className='btn-back'
        title='Back'
        onClick={e => routeToPath(`/?page=professional_details&id=${professionalId}`)}>
      </button>
      <div className='top-nav-spacer'></div>
      <button
        className='btn-save'
        title='Save project'
        onClick={saveHandler}></button>
    </div>

    <div className='white bg-red head'>{state.ID ? 'Edit' : 'New'} Project</div>
      {error && <div className='error'>⚠️ {error}</div>}
    <div className='modal-section'>
      <div className='form-inline'>
        <div>
          <div className='label'>
            Code
          </div>
          <div className='value'>
            <input
              onChange={(e: any) => setState({...state, ProjectCode: e.target.value})}
              value={state.ProjectCode} />
          </div>
        </div>
        <div>
          <div className='label'>
            Type
          </div>
          <div className='value'>
            <select value={state.ProjectType} onChange={e => setState(changeProjectType(state, e))}>
              <option value='Purchase'>Purchase</option>
              <option value='Sales'>Sales</option>
              <option value='Kickback'>Kickback</option>
            </select>
          </div>
        </div>
        <div>
          <div className='label'>
            Magno Entity
          </div>
          <div className='value'>
            <select value={state.MagnoEntity} onChange={e => setState({
              ...state,
              MagnoEntity: getSelectedValue(e) as MagnoEntityType
            })}>
              <option value='MIR'>MIR</option>
              <option value='MIP'>MIP</option>
              <option value='MIB'>MIB</option>
            </select>
          </div>
        </div>
        <div>
          <div className='label'>
            MIP project code
          </div>
          <div className='value'>
            <input
              onChange={(e: any) => setState({...state, MIPProjectCode: e.target.value})}
              value={state.MIPProjectCode || ''} />
          </div>
        </div>
        {state.ProjectType === 'Sales' && <div>
          <div className='label'>
            Unit type
          </div>
          <div className='value'>
            <select value={String(state.UnitType)} onChange={e => setState({
              ...state,
              UnitType: getSelectedValueOrNull(e) as AVProjectUnitType
            })}>
              <option value='null'>Select unit type</option>
              <option value='hours'>Hours</option>
              <option value='days'>Days</option>
            </select>
          </div>
        </div>}
      </div>
    </div>
    <div className='modal-section'>
      <div className='form-inline'>
        <div>
          <div className='label'>
            Start date
          </div>
          <div className='value'>
            <DateInput
              action={(e: DateInputUpdate) => setState({...state, StartDate: e.value})}
              id='endDate'
              value={state.StartDate} />
          </div>
        </div>
        <div>
          <div className='label'>
            End date
          </div>
          <div className='value'>
            <DateInput
              action={(e: DateInputUpdate) => setState({...state, EndDate: e.value})}
              id='startDate'
              value={state.EndDate || ''} />
          </div>
        </div>
        {state.ProjectType === 'Sales' && <div>
          <div className='label'>
            Cutoff point
          </div>
          <div className='value'>
            <select value={String(state.CutoffPoint)} onChange={e => setState({
              ...state,
              CutoffPoint: getSelectedValueOrNull(e) as AVProjectCutoffPoint
            })}>
              <option value='null'>Select cutoff point</option>
              <option value='LAST_FRIDAY_OF_MONTH'>Last Friday of month</option>
              <option value='LAST_CALENDAR_DAY_OF_MONTH'>Last calendar day of month</option>
            </select>
          </div>
        </div>}
      </div>
    </div>
    {state.ProjectType === 'Sales' && <div className='modal-section'>
      <div className='form-inline'>
        <div>
          <div className='label'>
            Supplier
          </div>
          <div className='value'>
            <select value={String(state.Supplier)} onChange={e =>
              setState(changeSupplier(state, e))
            }>
              <option value='null'>Select supplier</option>
              <option value='ComPas'>ComPas</option>
              <option value='MIR'>MIR</option>
              <option value='Other'>Other</option>
            </select>
          </div>
        </div>
        <div>
          <div className='label'>
            Client
          </div>
          <div className='value'>
            <select value={String(state.ClientCode)} onChange={e =>
              setState({
                ...state,
                ClientCode: getSelectedValueOrNull(e) as ClientCodeType | null
              })
            }>
              <option value='null'>Select client</option>
              <option value='LG'>LG</option>
              <option value='ING'>ING</option>
              <option value='RELX'>RELX</option>
              <option value='InfoSys'>InfoSys</option>
              <option value='VZ'>VZ</option>
            </select>
          </div>
        </div>
        {state.Supplier === 'Other' && <div>
          <div className='label'>
            Exact Supplier Code
          </div>
          <div className='value'>
            <input
              onChange={(e: any) => setState({...state, ExactSupplierCode: e.target.value})}
              value={state.ExactSupplierCode || ''} />
          </div>
        </div>}
      </div>
    </div>}
    {showExpensesControls && <div className='modal-section'>
      <div className='form-inline'>
        <ProjectBinaryAttribute
          label={'Expenses reimbursed'}
          value={String(state.ExpensesReimbursed)}
          onChange={e => setState({
            ...state,
            ExpensesReimbursed: getSelectedValueOrNull(e) === 'true'
          })}
        />
        <ProjectBinaryAttribute
          label={'KM reimbursed'}
          value={String(state.TravelReimbursed)}
          onChange={e => setState({
            ...state,
            TravelReimbursed: getSelectedValueOrNull(e) === 'true'
          })}
        />
      </div>
      <div className='form-inline'>
        <ProjectBinaryAttribute
          label={'Paid leave'}
          value={String(state.PaidLeave)}
          onChange={e => setState({
            ...state,
            PaidLeave: getSelectedValueOrNull(e) === 'true'
          })}
        />
        <ProjectBinaryAttribute
          label={'Travel exp. deductible'}
          value={String(state.TravelExpensesDeductible)}
          onChange={e => setState({
            ...state,
            TravelExpensesDeductible: getSelectedValueOrNull(e) === 'true'
          })}
        />
        <ProjectBinaryAttribute
          label={'Other exp. deductible'}
          value={String(state.ExpensesDeductible)}
          onChange={e => setState({
            ...state,
            ExpensesDeductible: getSelectedValueOrNull(e) === 'true'
          })}
        />
        <ProjectBinaryAttribute
          label={'KM deductible'}
          value={String(state.TravelDeductible)}
          onChange={e => setState({
            ...state,
            TravelDeductible: getSelectedValueOrNull(e) === 'true'
          })}
        />
        <ProjectNullableNumberAttribute
          label='Max deductible travel'
          value={state.MaxDeductibleTravel}
          onChange={value => setState({
            ...state,
            MaxDeductibleTravel: value
          })}
        />
      </div>
    </div>}
    
    {state.ProjectType === 'Sales' &&
      <ApproversSelector
        approvers={approvers}
        setApprovers={setApprovers}
      />}

    <div className='modal-info'></div>
  </div>
}
